import {
  KikApiCommonUtils,
  KikGenericResponse,
  KikHttpTwoConfig,
  KikRestVerbs,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { setConfig } from "next/config";
import { NextRouter } from "next/router";

import { HttpService } from "~/services/http";
import { ApiConstants } from "~/shared/api/api-constants.class";
import {
  KikReviewsCategoryResponse,
  KikReviewsTranslateV2ResponseItem,
  KikReviewsV2Response,
  KikReviewsV2ResponseReviewItem,
  KikReviewsWrapperV2Response,
} from "~/shared/power-reviews-response.interface";
import CommonUtils from "~/utils/common-utils";

import { KIKOApplicationConfiguration } from "../../global";

type PowerReviewsConfig = KIKOApplicationConfiguration["App:Ecommerce:PowerReviewsConfig"][0];

export type PropsPowerReviewsCultureConfig = PowerReviewsConfig | void;

const powerReviewUtils = {
  getPwrConfiguration(configuration: any, router: NextRouter): PropsPowerReviewsCultureConfig {
    if (!configuration || !router) {
      return void 0;
    }
    const parsedConfig: PowerReviewsConfig[] | void = CommonUtils.parseJsonSafely(
      configuration?.["App:Ecommerce:PowerReviewsConfig"] as unknown as string
    );

    return parsedConfig?.find(
      (conf: PowerReviewsConfig) => conf?.culture?.toLowerCase() === router?.locale?.toLowerCase()
    );
  },

  setConfig(url: string, locale: string, organizationUnitId: string) {
    HttpService.updateOrganizationUnitId(organizationUnitId);
    return {
      url: url,
      headers: { "x-locale": locale },
    } as KikHttpTwoConfig;
  },

  async getReviews(
    backendId: string,
    organizationUnitId: string,
    locale: string,
    params?: {
      pageFrom?: number;
      filters?: string;
    }
  ): Promise<KikReviewsV2Response | undefined> {
    const endpoint = ApiConstants.endpointsMapiV1.ecoReviewsBySku;
    const url = KikApiCommonUtils.interpolateParams(endpoint, { sku: `${backendId}` });
    const config = this.setConfig(url, locale, organizationUnitId);
    if (params) {
      config.queryParams = { ...params };
    }
    HttpService.updateOrganizationUnitId(organizationUnitId);
    const response = await HttpService.$http<KikGenericResponse<KikReviewsWrapperV2Response>>(config);
    return response?.data?.reviews;
  },

  async getReviewById(
    reviewId: string,
    organizationUnitId: string,
    locale: string
  ): Promise<KikReviewsV2ResponseReviewItem | undefined> {
    const endpoint = ApiConstants.endpointsMapiV1.ecoReview;
    const url = KikApiCommonUtils.interpolateParams(endpoint, { reviewId: reviewId });
    const config = this.setConfig(url, locale, organizationUnitId);
    const response = await HttpService.$http<KikGenericResponse<KikReviewsV2ResponseReviewItem>>(config);
    return response?.data;
  },

  async getReviewTranslation(
    reviewId: string,
    organizationUnitId: string,
    locale: string
  ): Promise<KikReviewsTranslateV2ResponseItem | undefined> {
    const endpoint = ApiConstants.endpointsMapiV1.ecoReviewTranslation;
    const targetLang = locale.split("-")[0] ?? "en";
    const url = KikApiCommonUtils.interpolateParams(endpoint, { reviewId: reviewId, targetLanguage: targetLang });
    const config = this.setConfig(url, locale, organizationUnitId);
    config.method = KikRestVerbs.POST;
    const response = await HttpService.$http<KikGenericResponse<KikReviewsTranslateV2ResponseItem>>(config);
    return response?.data;
  },

  async getCategoryReviews(
    categoryPath: string,
    organizationUnitId: string,
    locale: string
  ): Promise<KikReviewsCategoryResponse | undefined> {
    const endpoint = ApiConstants.endpointsMapiV1.ecoReviewsByCategory;
    const url = KikApiCommonUtils.interpolateParams(endpoint, { categoryPath: `${categoryPath}` });
    const config = this.setConfig(url, locale, organizationUnitId);
    const response = await HttpService.$http<KikGenericResponse<KikReviewsCategoryResponse>>(config);
    return response?.data;
  },

  getThumbnail(uri: string, extension?: string): string {
    const normalizedUri = uri.replace(/^\/\/res\.cloudinary\.com\/powerreviews/, "https://media.powerreviews.com");
    const thumbnailUri = normalizedUri.replace(
      /\/image\/upload\/[^/]+\//,
      "/image/upload/c_thumb,d_portal-no-product-image_ttlfpi.svg,h_150,q_auto,w_150/"
    );
    if (extension) {
      return thumbnailUri.replace(/\.[^.\/]+$/, `.${extension}`);
    } else {
      return thumbnailUri;
    }
  },
};

export default powerReviewUtils;
